import { Component, DestroyRef, HostListener, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { Skeleton } from 'primeng/skeleton';
import { WebsiteList } from '@/app/pages/website/list/website-list';
import { WebsiteInfo } from '@/app/pages/website/website-info/website-info';
import { RealmService } from '@/app/services/realm.service';
import { HasUnsavedChanges } from '@/app/guards/unsaved-changes.guard';
import { ProviderStatus, RealmConfig } from '@/lib-backend';

@Component({
    selector: 'app-website',
    standalone: true,
    imports: [
        WebsiteList,
        WebsiteInfo,
        Dialog,
        Button,
        Skeleton,
    ],
    templateUrl: './website.html',
    styleUrl: './website.scss',
})
export class Website implements OnInit, HasUnsavedChanges {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly realmService = inject(RealmService);
    private readonly destroyRef = inject(DestroyRef);

    readonly websiteInfo = viewChild<WebsiteInfo>('websiteInfo');

    selectedRealm = signal<RealmConfig | null>(null);
    selectedProviderStatus = signal<ProviderStatus | null>(null);
    realmLoading = signal(false);
    unsavedDialogVisible = signal(false);
    private unsavedResolve: ((value: boolean) => void) | null = null;

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
        }
    }

    ngOnInit(): void {
        this.route.paramMap
            .pipe(
                switchMap(async (params) => {
                    const realmId = params.get('realmId');
                    if (realmId) {
                        this.realmLoading.set(true);
                        this.selectedRealm.set(null);
                        this.selectedProviderStatus.set(null);
                        return await this.realmService.getRealm(realmId);
                    }
                    return null;
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((result) => {
                this.selectedRealm.set(result?.config ?? null);
                this.selectedProviderStatus.set(result?.providerStatus ?? null);
                this.realmLoading.set(false);
            });
    }

    hasUnsavedChanges(): boolean {
        return this.websiteInfo()?.hasUnsavedChanges() ?? false;
    }

    showUnsavedDialog(): Promise<boolean> {
        this.unsavedDialogVisible.set(true);
        return new Promise<boolean>((resolve) => {
            this.unsavedResolve = resolve;
        });
    }

    protected async onDialogSave(): Promise<void> {
        const info = this.websiteInfo();
        if (info) {
            await info.saveAll();
            if (!info.hasUnsavedChanges()) {
                this.unsavedDialogVisible.set(false);
                this.unsavedResolve?.(true);
                this.unsavedResolve = null;
            }
        }
    }

    protected onDialogDiscard(): void {
        this.unsavedDialogVisible.set(false);
        this.unsavedResolve?.(true);
        this.unsavedResolve = null;
    }

    protected onDialogCancel(): void {
        this.unsavedDialogVisible.set(false);
        this.unsavedResolve?.(false);
        this.unsavedResolve = null;
    }

    protected async backToList(): Promise<void> {
        if (this.hasUnsavedChanges()) {
            const canLeave = await this.showUnsavedDialog();
            if (!canLeave) return;
        }
        this.router.navigate(['/website']);
    }

    protected onRealmReassigned(config: RealmConfig): void {
        this.selectedRealm.set(config);
    }
}
